import { StandardEvents } from '@shopify/events';

// Essentials live outside ProductInformation. Keep their values, and card
// lumens, tied to the selected variant without adding another customer choice.
const pendingSelections = new WeakMap();

function shopifyId(value) {
  const id = String(value ?? '').split('/').pop();
  return /^\d+$/.test(id) ? id : '';
}

function readJson(scope, selector) {
  try {
    return JSON.parse(scope.querySelector(selector)?.textContent || 'null');
  } catch {
    return null;
  }
}

function updateDimensions(section, value) {
  if (typeof value !== 'string' || !value.trim()) return;
  for (const field of section.querySelectorAll('[data-tvm-dimensions]')) {
    if (field.textContent !== value) field.textContent = value;
  }
}

function updateLightOutput(scope, variantId, data) {
  const id = shopifyId(variantId);
  const lumens = data?.lumens?.[id];
  const validOutput = Number.isSafeInteger(lumens) && lumens > 0;
  const isCard = scope.hasAttribute('data-tvm-lumen-card');
  // A missing variant estimate must not retain the previous option's number.
  const value = validOutput
    ? `Approx. ${lumens}${isCard ? '' : ' lumens'}`
    : 'Not verified for this option';
  for (const field of scope.querySelectorAll('[data-tvm-light-output-value]')) {
    if (field.textContent !== value) field.textContent = value;
  }
  if (id) scope.dataset.selectedVariantId = id;
  else delete scope.dataset.selectedVariantId;
  if (isCard) {
    const prefix = scope.dataset.tvmLightPrefix;
    const outputLabel = validOutput
      ? `Reference light-output estimate: Approx. ${lumens} lumens`
      : 'Light output: Not verified for this option';
    scope.setAttribute('aria-label', `${prefix ? `${prefix}; ` : ''}${outputLabel}`);
  }
}

function selectedVariantId(picker, data, intendedId) {
  if (!Array.isArray(data?.variants) || !data.variants.length) return '';
  const optionCount = data.variants[0].options.length;
  const options = new Array(optionCount);
  // Option-value IDs preserve original Liquid text even when the visible
  // option names or labels are transformed (for example, Atman's light color).
  for (const selected of picker.querySelectorAll('input:checked[data-option-value-id], select option:checked')) {
    const option = data.optionValues?.[selected.dataset.optionValueId];
    if (option && Number.isInteger(option.position) && option.position >= 0 && option.position < optionCount) {
      options[option.position] = option.value;
    }
  }
  const isComplete = Array.from(options).every((option) => typeof option === 'string');
  if (isComplete) {
    // Do not reuse a stale data-variant-id after another option changed while
    // the previous request was still running; resolve the whole combination.
    return data.variants.find((variant) => variant.options.every((value, index) => value === options[index]))?.id || '';
  }
  // A collection swatch selects the theme's first available variant for that
  // swatch, while intentionally displaying only one of the product's options.
  return shopifyId(intendedId);
}

document.addEventListener(StandardEvents.productSelect, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const picker = target?.closest('variant-picker, swatches-variant-picker-component');
  if (!picker) return;

  const productId = shopifyId(picker.dataset.productId);
  const card = picker.closest('product-card');
  const isMainPicker = picker.dataset.templateProductMatch === 'true' &&
    !picker.closest('product-card, quick-add-dialog, featured-product-information');
  const selector = card ? '[data-tvm-lumen-card]' : '[data-tvm-product-specs]';
  const scopes = card
    ? Array.from(card.querySelectorAll(selector))
    : isMainPicker ? Array.from(document.querySelectorAll(selector)) : [];
  const sections = scopes.filter((scope) => scope.dataset.productId === productId);
  if (!sections.length) return;

  const selection = {};
  for (const section of sections) pendingSelections.set(section, selection);

  // Combined listings replace the whole main element. Invalidate any older
  // response above, then let the theme replace both product and specifications.
  const connectedUrl = event.detail?.connectedProductUrl;
  if (connectedUrl && connectedUrl.split('?')[0] !== picker.dataset.productUrl?.split('?')[0]) return;

  for (const section of sections) {
    const sizeValues = readJson(section, '[data-tvm-size-options]');
    if (sizeValues) {
      for (const selected of picker.querySelectorAll('input:checked[data-option-value-id], select option:checked')) {
        updateDimensions(section, sizeValues[selected.dataset.optionValueId]);
      }
    }
    const lightData = readJson(section, '[data-tvm-light-output-map]');
    updateLightOutput(section, selectedVariantId(picker, lightData, event.detail?.variantId), lightData);
  }

  if (!event.promise || typeof event.promise.then !== 'function') return;
  event.promise.then((result) => {
    const detail = result?.detail;
    if (!detail || shopifyId(detail.productId) !== productId || detail.newProduct) return;
    for (const section of sections) {
      if (
        !section.isConnected ||
        section.dataset.productId !== productId ||
        pendingSelections.get(section) !== selection
      ) continue;

      const nextSection = Array.from(detail.html?.querySelectorAll(selector) || []).find(
        (candidate) => candidate.dataset.productId === productId
      );
      if (nextSection) {
        updateDimensions(section, nextSection.querySelector('[data-tvm-dimensions]')?.textContent);
      } else if (section.hasAttribute('data-size-option-index')) {
        updateDimensions(section, detail.resource?.options?.[Number(section.dataset.sizeOptionIndex)]);
      }

      // Horizon resolves the actual selected variant in resource.id. Standard
      // event consumers may receive the GID in result.variant.id instead.
      const resolvedId = shopifyId(detail.resource?.id) || shopifyId(result.variant?.id);
      if (!resolvedId) continue;
      const lightData = (nextSection && readJson(nextSection, '[data-tvm-light-output-map]')) ||
        readJson(section, '[data-tvm-light-output-map]');
      updateLightOutput(section, resolvedId, lightData);
    }
  }).catch(() => {
    // Aborted requests are expected when customers change options quickly.
    // Preserve the latest local selection; the picker owns request errors.
  });
});
